import { useState } from 'react';
import { motion } from 'framer-motion';
import { Sparkles, ChevronDown, ChevronUp } from 'lucide-react';
import { useT } from '@/lib/i18n';
import { Loader } from '@/components/Loader';

interface Neighborhood {
  id: string;
  name: string;
}

interface NeighborhoodSelectorProps {
  neighborhoods: Neighborhood[];
  selectedNeighborhood: Neighborhood | null;
  onNeighborhoodSelect: (neighborhood: Neighborhood) => void;
  cityLabel?: string;
  isLoading?: boolean;
}

export function NeighborhoodSelector({
  neighborhoods,
  selectedNeighborhood,
  onNeighborhoodSelect,
  cityLabel,
  isLoading = false,
}: NeighborhoodSelectorProps) {
  const { t } = useT();
  const [showSelector, setShowSelector] = useState(true);

  const handleNeighborhoodSelect = (neighborhood: Neighborhood) => {
    onNeighborhoodSelect(neighborhood);
    setShowSelector(false);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.2 }}
      className="mb-6"
    >
      <button
        onClick={() => setShowSelector(!showSelector)}
        className="w-full flex items-center justify-between my-2 group"
      >
        <div className="flex items-center">
          <div className={`w-10 h-10 rounded-lg flex items-center justify-center mr-3 transition-colors ${
            selectedNeighborhood ? 'bg-primary-500/20' : 'bg-dark-elevated dark:bg-neutral-800'
          }`}>
            <Sparkles className={`w-5 h-5 transition-colors ${
              selectedNeighborhood ? 'text-primary-500' : 'text-dark-muted dark:text-neutral-400'
            }`} />
          </div>
          <div className="text-left">
            <div className="text-xs text-dark-muted dark:text-neutral-400 mb-0.5">{t('booking.neighborhood.selectNeighborhood')}</div>
            <div className="text-sm font-semibold dark:text-neutral-100">
              {selectedNeighborhood ? (
                <span className="capitalize">{selectedNeighborhood.name}</span>
              ) : (
                t('booking.neighborhood.selectNeighborhood')
              )}
            </div>
          </div>
        </div>
        {selectedNeighborhood && (
          showSelector ? (
            <ChevronUp className="w-5 h-5 text-dark-muted dark:text-neutral-400 group-hover:text-primary-800 dark:group-hover:text-primary-400 transition-colors" />
          ) : (
            <ChevronDown className="w-5 h-5 text-dark-muted dark:text-neutral-400 group-hover:text-primary-800 dark:group-hover:text-primary-400 transition-colors" />
          )
        )}
      </button>

      {(!selectedNeighborhood || showSelector) && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: 'auto' }}
          exit={{ opacity: 0, height: 0 }}
        >
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader />
            </div>
          ) : neighborhoods.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {neighborhoods.map((neighborhood) => (
                <button
                  key={neighborhood.id}
                  onClick={() => handleNeighborhoodSelect(neighborhood)}
                  className={`
                    p-4 rounded-lg transition-all text-left
                    ${
                      selectedNeighborhood?.id === neighborhood.id
                        ? 'select-option-selected'
                        : 'select-option'
                    }
                  `}
                >
                  <div className="font-semibold text-sm capitalize dark:text-neutral-100">
                    {neighborhood.name}
                  </div>
                </button>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-dark-muted dark:text-neutral-400">
              {cityLabel ? t('booking.neighborhood.noNeighborhoodsInCity', { city: cityLabel }) : t('booking.neighborhood.noNeighborhoods')}
            </div>
          )}
        </motion.div>
      )}
    </motion.div>
  );
}

